<script setup>
import { ref, computed, defineAsyncComponent, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
/***/
import { useApplicationStore } from '@stores/application';
import { useCreditPackStore } from '@stores/creditPack';
import { useCartStore } from '@stores/cart';
import { useTaskConfigurationStore } from '@stores/taskConfiguration';
/***/
import { useAuthUser } from '@compositions/Auth';
/***/
import { Stripe } from '@classes/Stripe';

// Composants
const Layout = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Layouts/Authenticated'));
const Loader = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Loader'));
const MenuTab = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Me/Credits/MenuTab'));
const CreditPurchase = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Me/Credits/CreditPurchase'));
const SubscriptionPurchase = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Me/Credits/SubscriptionPurchase'));

const router = useRouter();
const { t } = useI18n();

// Store
const applicationStore = useApplicationStore();
const creditPackStore = useCreditPackStore();
const taskConfigurationStore = useTaskConfigurationStore();
const cartStore = useCartStore();

// Retourne si la liste des packs de crédit a été chargée
const creditPackListInitialized = computed(() => !! creditPackStore.getCreditPackList.total);

// Vérification de l'existence des packs de crédit
const creditPackListByType = ((type) => creditPackStore.getCreditPackByType(type));
const hasUniqueCredits = computed(() => (creditPackListByType('UNIQUE').length > 0));
const hasSubscriptionPacks = computed(() => (creditPackListByType('MONTHLY').length > 0));

// Retourne si la liste des configurations de tâche a été chargée
const configurationTaskListInitialized = computed(() => taskConfigurationStore.getTaskConfigurationList !== null);

// Produits dans le panier
const cartProducts = computed(() => cartStore.getProducts);

// Retourne l'application courante
const currentApplication = computed(() => applicationStore.getCurrent);

// Composition pour l'utilisateur connecté
const { authUser, authUserHasPermission } = useAuthUser({});


const loadingCreditPacksError = ref(false);
const loadingCreditPacks = ref(true);
/***/
const loadingTaskConfigurationsError = ref(false);
const loadingTaskConfigurations = ref(true);
/***/
const loadingCartError = ref(false);
const loadingCart = ref(true);
/***/
const allowed = ref(false);
const ready = computed(() => (! loadingCreditPacks.value && ! loadingTaskConfigurations.value && ! loadingCart.value));

const currentTab = ref(null);

// Retourne le texte de la balise title dans le head
const metaTitle = (() => {
    let title = t('credits.purchase.title');

    if(currentTab.value !== null) {
        const metaTitleMessageKey = 'credits.purchase.:type.metaTitle'.replace(':type', currentTab.value.toLowerCase());
        title += ' - ' + t(metaTitleMessageKey);
    }

    return (title + ' - ' + currentApplication.value.name);
});

// Chargement de la liste des packs de crédit disponibles
// @return Promise
const loadCreditPackList = (() => {
    if(! creditPackListInitialized.value) {
        return creditPackStore.loadCreditPackList({}).then(() => {

        }).catch(() => {
            loadingCreditPacksError.value = true;
        }).finally(() => {
            loadingCreditPacks.value = false;
        });
    } else {
        loadingCreditPacks.value = false;
        return Promise.resolve();
    }
});

// Chargement de la liste des configurations de tâches
// @return Promise
const loadTaskConfigurationList = (() => {
    if(! configurationTaskListInitialized.value) {
        return taskConfigurationStore.loadTaskConfigurationList().then(() => {

        }).catch(() => {
            loadingTaskConfigurationsError.value = true;
        }).finally(() => {
            loadingTaskConfigurations.value = false;
        });
    } else {
        loadingTaskConfigurations.value = false;
        return Promise.resolve();
    }
});

// Chargement des produits dans le panier
// @return Promise
const loadCart = (() => {
    if(! cartProducts.value) {
        return cartStore.loadProducts().then(() => {

        }).catch(() => {
            loadingCartError.value = true;
        }).finally(() => {
            loadingCart.value = false;
        });
    } else {
        loadingCart.value = false;
        return Promise.resolve();
    }
});

// Méthode de changement d'onglet
const changeTab = ((tabName) => {
    currentTab.value = tabName;
    document.title = metaTitle();
});

// Chargement des packs de crédit, des configurations de tâches et du panier
const loadRequiredData = (() => {
    return loadCreditPackList().then(() => {
        return loadTaskConfigurationList().then(() => {
            return loadCart().then(() => {
                if(
                    hasSubscriptionPacks.value &&
                    authUserHasPermission('edit self credits subscription') &&
                    authUserHasPermission('to have credits subscription')
                ) {
                    changeTab('MONTHLY');
                } else if(hasUniqueCredits.value) {
                    changeTab('UNIQUE');
                }

                allowed.value = true;
            });
        });
    });
});

// Initialisation de stripe
const stripeInstance = Stripe.getInstance();
const initPage = (() => {
    stripeInstance.initSDK().then(() => {
        loadRequiredData();
    });
});

onMounted(() => {
    if(! (authUserHasPermission('to have credits') && authUserHasPermission('buy credits'))) {
        router.push({
            name: 'error',
            params: {
                statusCode: 404
            }
        });
    } else {
        initPage();
    }
});
</script>

<template>
    <loader v-if="! ready"></loader>
    <layout v-if="(loadingCreditPacksError || loadingTaskConfigurationsError || loadingCartError) || (allowed && ready)" wrapperContentClasses="wrapper_edit-add">
        <div class="content_header">
            <div class="content-header_title">
                <h1 class="first-title">{{ $t('credits.purchase.title') }}</h1>
                <p>{{ $t('credits.purchase.description') }}</p>
            </div>
        </div>

        <p
            v-if="loadingTaskConfigurationsError"
            class="generic-message generic-message-error"
        >
            {{ $t('credits.purchase.loading.error.taskConfiguration') }}
        </p>

        <p
            v-else-if="loadingCreditPacksError"
            class="generic-message generic-message-error"
        >
            {{ $t('credits.purchase.loading.error.creditPacks') }}
        </p>

        <p
            v-else-if="loadingCartError"
            class="generic-message generic-message-error"
        >
            {{ $t('credits.purchase.loading.error.cart') }}
        </p>

        <p
            v-else-if="currentTab === null"
            class="generic-message generic-message-error"
        >
            {{ $t('credits.purchase.loading.error.empty') }}
        </p>

        <template v-else>
            <menu-tab
                v-if="currentTab !== null"
                :active-tab="currentTab"
                v-on:change-tab="changeTab($event)"
            ></menu-tab>
            <div class="wrapper_content ">
                <credit-purchase
                    v-show="currentTab === 'UNIQUE'"
                ></credit-purchase>

                <subscription-purchase
                    v-show="currentTab === 'MONTHLY'"
                ></subscription-purchase>
            </div>
        </template>

    </layout>
</template>
